// src/pages/LegalNotice.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";

const OPERATOR = {
  platformName: "ZEDRO Academy",
  legalOwnerName: "Rodríguez Díaz, Catalina",
  legalTradeName: "Académie ZEDRO",
  legalFormEs: "empresa individual",
  legalFormFr: "entreprise individuelle",
  neq: "2282259599",
  websiteLabel: "zedro.academy",
  websiteUrl: "https://zedro.academy",
  supportEmail: "support@zedro.academy",
  adminEmail: "admin@zedro.academy",
  addressEs: "412-9165 rue Lennon, Brossard, Québec, Canadá, J4Y 0R9",
  addressFr: "412-9165, rue Lennon, Brossard (Québec) J4Y 0R9, Canada",
};

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";

  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return ["es", "fr"].includes(savedLanguage) ? savedLanguage : "es";
}

const pageStyle = {
  maxWidth: 900,
  margin: "2rem auto",
  padding: "0 1rem",
  fontFamily: "Questrial, sans-serif",
  lineHeight: 1.75,
  color: "#2c3b51",
};

const sectionTitleStyle = {
  color: "#264763",
};

const LEGAL_NOTICE_COPY = {
  es: {
    title: "Aviso legal",
    lastUpdatedLabel: "Última actualización:",
    lastUpdated: "17 de julio de 2026",
    intro: [
      <>
        Este Aviso legal identifica a la persona que opera{" "}
        <strong>{OPERATOR.platformName}</strong> y establece la información
        jurídica general aplicable al sitio{" "}
        <a href={OPERATOR.websiteUrl}>{OPERATOR.websiteLabel}</a>.
      </>,
      <>
        Este documento complementa los{" "}
        <Link to="/legal/terminos">
          Términos y Condiciones de Uso, Compra y Suscripción
        </Link>
        , la <Link to="/legal/privacidad">Política de Privacidad</Link> y la{" "}
        <Link to="/legal/cookies">Política de Cookies</Link>. Las condiciones
        específicas de una compra, membresía o servicio se comunican antes de
        confirmar la transacción.
      </>,
    ],
    sections: [
      {
        title: "1. Identificación del operador",
        paragraphs: [
          <>
            Titular de la empresa individual:{" "}
            <strong>{OPERATOR.legalOwnerName}</strong>
          </>,
          <>
            Nombre comercial registrado:{" "}
            <strong>{OPERATOR.legalTradeName}</strong>
          </>,
          <>
            Número de empresa de Québec (NEQ):{" "}
            <strong>{OPERATOR.neq}</strong>
          </>,
          <>
            Plataforma: <strong>{OPERATOR.platformName}</strong>
          </>,
          <>
            Dirección comercial y postal:{" "}
            <strong>{OPERATOR.addressEs}</strong>
          </>,
          <>
            Sitio web:{" "}
            <a href={OPERATOR.websiteUrl}>{OPERATOR.websiteLabel}</a>
          </>,
          <>
            Soporte al cliente:{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>
              {OPERATOR.supportEmail}
            </a>
          </>,
          <>
            Asuntos administrativos y legales:{" "}
            <a href={`mailto:${OPERATOR.adminEmail}`}>
              {OPERATOR.adminEmail}
            </a>
          </>,
        ],
      },
      {
        title: "2. Alcance del sitio",
        paragraphs: [
          <>
            {OPERATOR.platformName} es un ecosistema digital operado por la
            titular de {OPERATOR.legalTradeName}. Puede incluir páginas públicas,
            cuentas de usuario, contenidos, productos digitales, membresías,
            herramientas y otros servicios relacionados con su objeto formativo.
          </>,
          <>
            La oferta, estructura y disponibilidad de los productos, contenidos
            y funciones pueden evolucionar. La descripción y las condiciones
            aplicables a cada oferta se presentan en la plataforma antes de que
            la persona realice una compra o suscripción.
          </>,
          <>
            La simple consulta de las páginas públicas no crea por sí sola una
            membresía ni una obligación de pago. Una transacción se completa
            únicamente mediante el proceso de compra o suscripción
            correspondiente.
          </>,
        ],
      },
      {
        title: "3. Propiedad intelectual",
        paragraphs: [
          <>
            Salvo indicación contraria, los textos, materiales de formación,
            metodologías, estructuras, nombres originales, diseños, imágenes,
            audios, videos, interfaces, software, código, recursos y demás
            elementos originales de {OPERATOR.platformName} pertenecen a la
            titular de {OPERATOR.legalTradeName} o se utilizan con autorización
            de sus respectivos titulares.
          </>,
          <>
            El acceso al sitio no transfiere derechos de propiedad intelectual.
            Solo se permite el uso personal autorizado por la plataforma, por los
            Términos y Condiciones o por la ley.
          </>,
          <>
            Sin autorización previa y escrita, no se permite copiar, reproducir,
            modificar, publicar, distribuir, vender, sublicenciar, explotar
            comercialmente ni reutilizar de forma sustancial esos elementos,
            salvo una excepción expresamente reconocida por la ley.
          </>,
          <>
            Las marcas, nombres, logotipos y contenidos pertenecientes a terceros
            continúan siendo propiedad de sus respectivos titulares.
          </>,
        ],
      },
      {
        title: "4. Uso del sitio y seguridad",
        paragraphs: [
          <>
            El sitio debe utilizarse de forma lícita y sin afectar su seguridad,
            integridad, disponibilidad ni los derechos de otras personas.
          </>,
        ],
        list: [
          "No intentar acceder a cuentas, datos, sistemas o áreas sin autorización.",
          "No introducir malware, código dañino o mecanismos destinados a interferir con el servicio.",
          "No realizar extracción automatizada, ingeniería inversa o reproducción técnica no autorizada.",
          "No compartir credenciales ni facilitar el uso no autorizado de una cuenta.",
          "No utilizar el sitio para actividades fraudulentas, ilegales o que vulneren derechos de terceros.",
        ],
      },
      {
        title: "5. Naturaleza del contenido",
        paragraphs: [
          <>
            Los contenidos y herramientas de {OPERATOR.platformName} tienen una
            finalidad formativa, educativa, reflexiva e informativa.
          </>,
          <>
            No constituyen asesoría médica, psicológica, legal, financiera,
            contable, terapéutica, profesional o pastoral personalizada, ni
            sustituyen la atención de una persona profesional calificada cuando
            esta sea necesaria.
          </>,
          <>
            La plataforma no promete resultados personales, espirituales,
            emocionales, económicos, profesionales o de otra naturaleza. Los
            resultados pueden variar según las decisiones, circunstancias y
            aplicación de cada persona.
          </>,
        ],
      },
      {
        title: "6. Productos, compras y membresías",
        paragraphs: [
          <>
            {OPERATOR.platformName} puede ofrecer productos digitales,
            membresías, suscripciones y otros servicios. La descripción, el
            precio, la moneda, los impuestos aplicables, la modalidad de pago,
            la periodicidad, el acceso y las demás condiciones esenciales se
            muestran antes de confirmar cada transacción.
          </>,
          <>
            Los detalles sobre contratación, renovación, cancelación, derechos
            del consumidor y reembolsos se encuentran en los{" "}
            <Link to="/legal/terminos">
              Términos y Condiciones de Uso, Compra y Suscripción
            </Link>
            .
          </>,
          <>
            Los pagos son procesados por Stripe u otro proveedor habilitado.{" "}
            {OPERATOR.platformName} no almacena números completos de tarjetas ni
            códigos completos de seguridad del método de pago.
          </>,
        ],
      },
      {
        title: "7. Idiomas",
        paragraphs: [
          <>
            Las páginas públicas, la información comercial esencial y los
            documentos legales están disponibles en francés y español.
          </>,
          <>
            El contenido principal de formación se ofrece actualmente en
            español. Esta condición se comunica antes de iniciar una membresía o
            adquirir una oferta que incluya dicho contenido.
          </>,
          <>
            Las reglas aplicables a la lengua del contrato se describen en los{" "}
            <Link to="/legal/terminos">
              Términos y Condiciones de Uso, Compra y Suscripción
            </Link>
            .
          </>,
        ],
      },
      {
        title: "8. Privacidad y cookies",
        paragraphs: [
          <>
            El tratamiento de información personal se describe en la{" "}
            <Link to="/legal/privacidad">Política de Privacidad</Link>.
          </>,
          <>
            El uso de cookies y tecnologías similares, así como las opciones de
            consentimiento y configuración, se explica en la{" "}
            <Link to="/legal/cookies">Política de Cookies</Link>.
          </>,
        ],
      },
      {
        title: "9. Servicios y enlaces de terceros",
        paragraphs: [
          <>
            La plataforma puede utilizar o enlazar servicios externos necesarios
            para pagos, alojamiento, almacenamiento, correo, analítica,
            seguridad, inteligencia artificial u otras funciones.
          </>,
          <>
            Los sitios y servicios externos se rigen por sus propias condiciones
            y políticas. {OPERATOR.legalTradeName} no controla su contenido ni
            su funcionamiento, sin perjuicio de las responsabilidades que la ley
            le imponga respecto de los proveedores que utiliza.
          </>,
        ],
      },
      {
        title: "10. Disponibilidad y evolución del servicio",
        paragraphs: [
          <>
            {OPERATOR.legalTradeName} procura mantener el sitio disponible,
            seguro y funcional, pero no puede garantizar un funcionamiento
            ininterrumpido ni ausencia absoluta de errores.
          </>,
          <>
            Pueden realizarse mantenimientos, correcciones, actualizaciones,
            mejoras o suspensiones temporales por razones técnicas, operativas,
            legales o de seguridad.
          </>,
          <>
            Estas actuaciones no limitan los derechos obligatorios de las
            personas consumidoras ni las obligaciones asumidas respecto de una
            transacción ya confirmada.
          </>,
        ],
      },
      {
        title: "11. Responsabilidad",
        paragraphs: [
          <>
            En la medida permitida por la ley,{" "}
            {OPERATOR.legalTradeName} no responde por pérdidas indirectas
            derivadas de eventos fuera de su control razonable, del uso no
            autorizado de una cuenta, de una utilización contraria a los
            documentos legales o de decisiones tomadas exclusivamente a partir
            de información general.
          </>,
          <>
            Nada en este Aviso excluye o limita la responsabilidad por falta
            intencional o falta grave, por perjuicio corporal o moral, ni los
            derechos u obligaciones que no puedan excluirse o limitarse conforme
            a la ley.
          </>,
        ],
      },
      {
        title: "12. Ley aplicable",
        paragraphs: [
          <>
            Este Aviso se interpreta conforme a las leyes aplicables en la
            provincia de Québec y a las leyes federales de Canadá que
            correspondan.
          </>,
          <>
            Esta disposición no limita los derechos obligatorios ni los recursos
            disponibles para una persona consumidora conforme a la ley aplicable
            o a su lugar de residencia.
          </>,
        ],
      },
      {
        title: "13. Actualizaciones",
        paragraphs: [
          <>
            Este Aviso puede actualizarse para reflejar cambios legales,
            técnicos, operativos o relacionados con los servicios ofrecidos. La
            versión vigente es la publicada en el sitio con su fecha de
            actualización.
          </>,
        ],
      },
      {
        title: "14. Contacto",
        paragraphs: [
          <>
            Para consultas generales o soporte:{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>
              {OPERATOR.supportEmail}
            </a>
          </>,
          <>
            Para asuntos administrativos o legales:{" "}
            <a href={`mailto:${OPERATOR.adminEmail}`}>
              {OPERATOR.adminEmail}
            </a>
          </>,
        ],
      },
    ],
  },

  fr: {
    title: "Avis juridique",
    lastUpdatedLabel: "Dernière mise à jour :",
    lastUpdated: "17 juillet 2026",
    intro: [
      <>
        Le présent Avis juridique identifie la personne qui exploite{" "}
        <strong>{OPERATOR.platformName}</strong> et présente les renseignements
        juridiques généraux applicables au site{" "}
        <a href={OPERATOR.websiteUrl}>{OPERATOR.websiteLabel}</a>.
      </>,
      <>
        Il complète les{" "}
        <Link to="/legal/terminos">
          Conditions d’utilisation, d’achat et d’abonnement
        </Link>
        , la <Link to="/legal/privacidad">Politique de confidentialité</Link> et
        la <Link to="/legal/cookies">Politique de cookies</Link>. Les conditions
        propres à un achat, à un abonnement ou à un service sont communiquées
        avant la confirmation de la transaction.
      </>,
    ],
    sections: [
      {
        title: "1. Identification de l’exploitante",
        paragraphs: [
          <>
            Titulaire de l’entreprise individuelle :{" "}
            <strong>{OPERATOR.legalOwnerName}</strong>
          </>,
          <>
            Nom commercial enregistré :{" "}
            <strong>{OPERATOR.legalTradeName}</strong>
          </>,
          <>
            Numéro d’entreprise du Québec (NEQ) :{" "}
            <strong>{OPERATOR.neq}</strong>
          </>,
          <>
            Plateforme : <strong>{OPERATOR.platformName}</strong>
          </>,
          <>
            Adresse commerciale et postale :{" "}
            <strong>{OPERATOR.addressFr}</strong>
          </>,
          <>
            Site Web :{" "}
            <a href={OPERATOR.websiteUrl}>{OPERATOR.websiteLabel}</a>
          </>,
          <>
            Service à la clientèle :{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>
              {OPERATOR.supportEmail}
            </a>
          </>,
          <>
            Questions administratives et juridiques :{" "}
            <a href={`mailto:${OPERATOR.adminEmail}`}>
              {OPERATOR.adminEmail}
            </a>
          </>,
        ],
      },
      {
        title: "2. Portée du site",
        paragraphs: [
          <>
            {OPERATOR.platformName} est un écosystème numérique exploité par la
            titulaire d’{OPERATOR.legalTradeName}. Il peut comprendre des pages
            publiques, des comptes utilisateurs, des contenus, des produits
            numériques, des abonnements, des outils et d’autres services liés à
            sa vocation de formation.
          </>,
          <>
            L’offre, la structure et la disponibilité des produits, contenus et
            fonctions peuvent évoluer. La description et les conditions
            applicables à chaque offre sont présentées dans la plateforme avant
            qu’une personne effectue un achat ou souscrive un abonnement.
          </>,
          <>
            La simple consultation des pages publiques ne crée, à elle seule,
            aucun abonnement ni aucune obligation de paiement. Une transaction
            est conclue uniquement au moyen du processus d’achat ou d’abonnement
            applicable.
          </>,
        ],
      },
      {
        title: "3. Propriété intellectuelle",
        paragraphs: [
          <>
            Sauf indication contraire, les textes, documents de formation,
            méthodologies, structures, noms originaux, designs, images, contenus
            audio et vidéo, interfaces, logiciels, code, ressources et autres
            éléments originaux de {OPERATOR.platformName} appartiennent à la
            titulaire d’{OPERATOR.legalTradeName} ou sont utilisés avec
            l’autorisation de leurs titulaires respectifs.
          </>,
          <>
            L’accès au site ne transfère aucun droit de propriété
            intellectuelle. Seul l’usage personnel autorisé par la plateforme,
            les Conditions ou la loi est permis.
          </>,
          <>
            Sans autorisation écrite préalable, il est interdit de copier,
            reproduire, modifier, publier, distribuer, vendre, concéder en
            sous-licence, exploiter commercialement ou réutiliser de façon
            substantielle ces éléments, sauf exception expressément reconnue par
            la loi.
          </>,
          <>
            Les marques, noms, logos et contenus appartenant à des tiers
            demeurent la propriété de leurs titulaires respectifs.
          </>,
        ],
      },
      {
        title: "4. Utilisation du site et sécurité",
        paragraphs: [
          <>
            Le site doit être utilisé de façon licite, sans porter atteinte à sa
            sécurité, à son intégrité, à sa disponibilité ni aux droits d’autrui.
          </>,
        ],
        list: [
          "Ne pas tenter d’accéder sans autorisation à des comptes, données, systèmes ou zones protégées.",
          "Ne pas introduire de logiciel malveillant, de code nuisible ou de mécanisme destiné à perturber le service.",
          "Ne pas effectuer d’extraction automatisée, d’ingénierie inverse ou de reproduction technique non autorisée.",
          "Ne pas partager des identifiants ni faciliter l’utilisation non autorisée d’un compte.",
          "Ne pas utiliser le site à des fins frauduleuses ou illégales ni d’une manière portant atteinte aux droits de tiers.",
        ],
      },
      {
        title: "5. Nature du contenu",
        paragraphs: [
          <>
            Les contenus et outils de {OPERATOR.platformName} sont fournis à des
            fins de formation, d’éducation, de réflexion et d’information.
          </>,
          <>
            Ils ne constituent pas un avis médical, psychologique, juridique,
            financier, comptable, thérapeutique ou professionnel ni un
            accompagnement pastoral personnalisé, et ne remplacent pas les
            services d’une personne professionnelle qualifiée lorsque ceux-ci
            sont nécessaires.
          </>,
          <>
            La plateforme ne promet aucun résultat personnel, spirituel,
            émotionnel, économique, professionnel ou autre. Les résultats
            peuvent varier selon les décisions, la situation et la mise en
            pratique de chaque personne.
          </>,
        ],
      },
      {
        title: "6. Produits, achats et abonnements",
        paragraphs: [
          <>
            {OPERATOR.platformName} peut offrir des produits numériques, des
            abonnements et d’autres services. La description, le prix, la
            devise, les taxes applicables, le mode de paiement, la fréquence de
            facturation, l’accès et les autres conditions essentielles sont
            affichés avant la confirmation de chaque transaction.
          </>,
          <>
            Les détails concernant la conclusion du contrat, le renouvellement,
            l’annulation, les droits des consommateurs et les remboursements se
            trouvent dans les{" "}
            <Link to="/legal/terminos">
              Conditions d’utilisation, d’achat et d’abonnement
            </Link>
            .
          </>,
          <>
            Les paiements sont traités par Stripe ou par un autre fournisseur
            activé. {OPERATOR.platformName} ne conserve pas les numéros complets
            de carte ni les codes de sécurité complets du mode de paiement.
          </>,
        ],
      },
      {
        title: "7. Langues",
        paragraphs: [
          <>
            Les pages publiques, les renseignements commerciaux essentiels et
            les documents juridiques sont offerts en français et en espagnol.
          </>,
          <>
            Le contenu principal de formation est actuellement offert en
            espagnol. Cette condition est communiquée avant la souscription d’un
            abonnement ou l’achat d’une offre comprenant ce contenu.
          </>,
          <>
            Les règles relatives à la langue du contrat sont décrites dans les{" "}
            <Link to="/legal/terminos">
              Conditions d’utilisation, d’achat et d’abonnement
            </Link>
            .
          </>,
        ],
      },
      {
        title: "8. Vie privée et cookies",
        paragraphs: [
          <>
            Le traitement des renseignements personnels est décrit dans la{" "}
            <Link to="/legal/privacidad">Politique de confidentialité</Link>.
          </>,
          <>
            L’utilisation des cookies et des technologies similaires, ainsi que
            les choix de consentement et de configuration, sont expliqués dans
            la <Link to="/legal/cookies">Politique de cookies</Link>.
          </>,
        ],
      },
      {
        title: "9. Services et liens de tiers",
        paragraphs: [
          <>
            La plateforme peut utiliser des services externes ou contenir des
            liens vers ceux-ci, notamment pour les paiements, l’hébergement, le
            stockage, le courriel, l’analytique, la sécurité, l’intelligence
            artificielle ou d’autres fonctions.
          </>,
          <>
            Les sites et services externes sont régis par leurs propres
            conditions et politiques. {OPERATOR.legalTradeName} ne contrôle pas
            leur contenu ni leur fonctionnement, sous réserve des
            responsabilités que la loi lui impose relativement aux fournisseurs
            qu’elle utilise.
          </>,
        ],
      },
      {
        title: "10. Disponibilité et évolution du service",
        paragraphs: [
          <>
            {OPERATOR.legalTradeName} s’efforce de maintenir le site accessible,
            sécuritaire et fonctionnel, sans pouvoir garantir un fonctionnement
            ininterrompu ni l’absence absolue d’erreurs.
          </>,
          <>
            Des travaux d’entretien, corrections, mises à jour, améliorations ou
            interruptions temporaires peuvent être effectués pour des raisons
            techniques, opérationnelles, juridiques ou de sécurité.
          </>,
          <>
            Ces interventions ne limitent pas les droits impératifs des
            consommateurs ni les obligations assumées relativement à une
            transaction déjà confirmée.
          </>,
        ],
      },
      {
        title: "11. Responsabilité",
        paragraphs: [
          <>
            Dans la mesure permise par la loi,{" "}
            {OPERATOR.legalTradeName} n’est pas responsable des pertes indirectes
            découlant d’événements hors de son contrôle raisonnable, de
            l’utilisation non autorisée d’un compte, d’une utilisation contraire
            aux documents juridiques ou de décisions prises exclusivement à
            partir de renseignements généraux.
          </>,
          <>
            Rien dans le présent Avis n’exclut ni ne limite la responsabilité
            découlant d’une faute intentionnelle ou d’une faute lourde, d’un
            préjudice corporel ou moral, ni les droits ou obligations qui ne
            peuvent être exclus ou limités par la loi.
          </>,
        ],
      },
      {
        title: "12. Droit applicable",
        paragraphs: [
          <>
            Le présent Avis est interprété conformément aux lois applicables
            dans la province de Québec et aux lois fédérales du Canada qui
            s’appliquent.
          </>,
          <>
            Cette disposition ne limite pas les droits impératifs ni les recours
            dont dispose une personne consommatrice en vertu de la loi applicable
            ou de son lieu de résidence.
          </>,
        ],
      },
      {
        title: "13. Mises à jour",
        paragraphs: [
          <>
            Le présent Avis peut être mis à jour afin de refléter des changements
            juridiques, techniques, opérationnels ou apportés aux services
            offerts. La version en vigueur est celle publiée sur le site avec sa
            date de mise à jour.
          </>,
        ],
      },
      {
        title: "14. Coordonnées",
        paragraphs: [
          <>
            Pour les questions générales ou le soutien :{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>
              {OPERATOR.supportEmail}
            </a>
          </>,
          <>
            Pour les questions administratives ou juridiques :{" "}
            <a href={`mailto:${OPERATOR.adminEmail}`}>
              {OPERATOR.adminEmail}
            </a>
          </>,
        ],
      },
    ],
  },
};

function renderParagraphs(paragraphs = []) {
  return paragraphs.map((paragraph, index) => (
    <p key={index}>{paragraph}</p>
  ));
}

function renderList(items = []) {
  if (!items.length) return null;

  return (
    <ul>
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

export default function LegalNotice() {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    if (typeof window === "undefined") return;

    function syncLanguage(nextLanguage) {
      if (["es", "fr"].includes(nextLanguage)) {
        setLanguage(nextLanguage);
      } else {
        setLanguage("es");
      }
    }

    function handleLanguageEvent(event) {
      syncLanguage(event?.detail?.language);
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        syncLanguage(event.newValue);
      }
    }

    syncLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
    window.addEventListener("zedro:languagechange", handleLanguageEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("zedro:languagechange", handleLanguageEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const copy = LEGAL_NOTICE_COPY[language] || LEGAL_NOTICE_COPY.es;

  return (
    <div style={pageStyle}>
      <h1>{copy.title}</h1>

      <p>
        <strong>{copy.lastUpdatedLabel}</strong> {copy.lastUpdated}
      </p>

      {renderParagraphs(copy.intro)}

      {copy.sections.map((section) => (
        <section key={section.title}>
          <h2 style={sectionTitleStyle}>{section.title}</h2>
          {renderParagraphs(section.paragraphs)}
          {renderList(section.list)}
        </section>
      ))}
    </div>
  );
}
